import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Play,
  Pause,
  Volume2,
  ChevronLeft,
  ChevronRight,
  Layers,
  Sparkles,
} from "lucide-react";
import { Slider } from "../ui/slider";
import {
  SOUND_PRESETS,
  DEFAULT_SOUND_ID,
  SOUND_STORAGE_KEY,
  getSoundPresetById,
  getSoundPresetIndex,
  CURATED_BLENDS,
  findMatchingBlend,
  buildEffectiveBlends,
  getSceneForBlend,
} from "@/lib/soundPresets";
import { logSoundEvent } from "@/lib/telemetry";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOP_BLENDS_URL = `${BACKEND_URL}/api/sound-events/top-blends?limit=6&min_count=3`;

const COUPLING_HINT_STORAGE_KEY = "stillwater.couplingHintSeen";

const hasSeenCouplingHint = () => {
  try {
    return localStorage.getItem(COUPLING_HINT_STORAGE_KEY) === "true";
  } catch (_) {
    return true; // fail closed — never annoy the user
  }
};

const markCouplingHintSeen = () => {
  try {
    localStorage.setItem(COUPLING_HINT_STORAGE_KEY, "true");
  } catch (_) { /* silent */ }
};

// Multi-preset in-browser ambient synthesizer with an optional "mix" mode:
//   - Primary voice is always active.
//   - Secondary voice is added when the Layers toggle is on, each played at
//     a reduced gain so they blend without over-competing.
//   - Chevrons cycle whichever voice currently has focus (primary by default,
//     secondary while mix is on) so exploring a blend stays one tap away.

const SECONDARY_STORAGE_KEY = "stillwater.soundSecondary";
const LAYER_STORAGE_KEY = "stillwater.soundLayerOn";
const WAS_PLAYING_KEY = "stillwater.wasPlaying";
const LAST_PLACE_NAME_KEY = "stillwater.lastPlaceName";

const FADE_SECONDS = 1.6;
const PRIMARY_MIX_GAIN = 0.7;
const SECONDARY_MIX_GAIN = 0.55;

const loadPrimary = () => {
  try {
    const raw = localStorage.getItem(SOUND_STORAGE_KEY);
    if (raw && getSoundPresetById(raw).id === raw) return raw;
  } catch (_) { /* silent */ }
  return DEFAULT_SOUND_ID;
};

const loadSecondary = (primaryId) => {
  try {
    const raw = localStorage.getItem(SECONDARY_STORAGE_KEY);
    if (raw && raw !== primaryId && getSoundPresetById(raw).id === raw) return raw;
  } catch (_) { /* silent */ }
  // Default to the next preset in the list for a pleasant first blend.
  const idx = getSoundPresetIndex(primaryId);
  return SOUND_PRESETS[(idx + 1) % SOUND_PRESETS.length].id;
};

const loadLayerOn = () => {
  try {
    return localStorage.getItem(LAYER_STORAGE_KEY) === "true";
  } catch (_) {
    return false;
  }
};

export const AudioControls = forwardRef((props, ref) => {
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(35);
  const [primaryId, setPrimaryId] = useState(loadPrimary);
  const [secondaryId, setSecondaryId] = useState(() => loadSecondary(loadPrimary()));
  const [layerOn, setLayerOn] = useState(loadLayerOn);
  // Curated blends start with the handpicked seed list; once we hear back
  // from the backend, popular organic pairings are promoted in.
  const [effectiveBlends, setEffectiveBlends] = useState(CURATED_BLENDS);
  // True once real anonymous top-blend data has arrived — gates the
  // "loved by others" community chips so we never fake popularity.
  const [liveTopLoaded, setLiveTopLoaded] = useState(false);
  // Track whether the user has taken manual control of the audio. Scene-based
  // auto-coupling only fires while this is false, so we never override a
  // choice the user has actually made.
  const [userEngagedAudio, setUserEngagedAudio] = useState(false);
  const userEngagedRef = useRef(false);
  userEngagedRef.current = userEngagedAudio;
  // When playback starts via a scene coupling, we play at a softer fraction
  // of the user's volume until they interact — then it snaps back to full.
  const [autoSoft, setAutoSoft] = useState(false);
  const autoSoftRef = useRef(autoSoft);
  autoSoftRef.current = autoSoft;

  const engage = useCallback(() => {
    if (!userEngagedRef.current) setUserEngagedAudio(true);
    if (autoSoftRef.current) setAutoSoft(false);
  }, []);

  // Fetch live top-blends once. Falls back silently to the seed list on any
  // error — this feature is never load-bearing.
  useEffect(() => {
    let cancelled = false;
    fetch(TOP_BLENDS_URL)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (cancelled) return;
        const top = data?.top || [];
        const next = buildEffectiveBlends(top, 5);
        if (next && next.length > 0) setEffectiveBlends(next);
        if (top.length > 0) setLiveTopLoaded(true);
      })
      .catch(() => { /* silent — keep seed list */ });
    return () => { cancelled = true; };
  }, []);

  const ctxRef = useRef(null);
  const masterRef = useRef(null);
  // A post-master gain node used purely for breath-synced modulation.
  // Sits between master and destination so it multiplies whatever the user's
  // volume + autoSoft settings produce, without racing with them. Idle value
  // is 1.0 — a no-op unless a breathing session tells us otherwise.
  const breathModRef = useRef(null);
  // Per-voice audio state: { gainNode, cleanupFn, presetId }
  const primaryRef = useRef(null);
  const secondaryRef = useRef(null);
  const switchingRef = useRef({ primary: false, secondary: false });

  const primary = getSoundPresetById(primaryId);
  const secondary = getSoundPresetById(secondaryId);

  const ensureContext = useCallback(() => {
    if (ctxRef.current) return ctxRef.current;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioCtx();
    const master = ctx.createGain();
    master.gain.value = 0.0001;
    const breathMod = ctx.createGain();
    breathMod.gain.value = 1.0; // no-op until a breathing session engages it
    master.connect(breathMod);
    breathMod.connect(ctx.destination);
    ctxRef.current = ctx;
    masterRef.current = master;
    breathModRef.current = breathMod;
    return ctx;
  }, []);

  const masterTarget = useCallback(() => {
    const base = (volume / 100) * 0.6;
    return autoSoft ? base * 0.35 : base;
  }, [volume, autoSoft]);

  const voiceTarget = useCallback(
    (kind) => {
      if (!layerOn) return 1.0;
      return kind === "primary" ? PRIMARY_MIX_GAIN : SECONDARY_MIX_GAIN;
    },
    [layerOn],
  );

  const buildVoice = useCallback((kind, presetId) => {
    const ctx = ensureContext();
    const voiceRef = kind === "primary" ? primaryRef : secondaryRef;
    // Tear down existing voice if present.
    if (voiceRef.current) {
      try { voiceRef.current.cleanupFn && voiceRef.current.cleanupFn(); } catch (_) { /* noop */ }
      try { voiceRef.current.gainNode.disconnect(); } catch (_) { /* noop */ }
      voiceRef.current = null;
    }
    const gainNode = ctx.createGain();
    gainNode.gain.value = 0.0001; // faded in explicitly
    gainNode.connect(masterRef.current);
    const cleanupFn = getSoundPresetById(presetId).build(ctx, gainNode);
    voiceRef.current = { gainNode, cleanupFn, presetId };
  }, [ensureContext]);

  const destroyVoice = useCallback((kind) => {
    const voiceRef = kind === "primary" ? primaryRef : secondaryRef;
    if (!voiceRef.current) return;
    const ctx = ctxRef.current;
    if (!ctx) return;
    const { gainNode, cleanupFn } = voiceRef.current;
    const now = ctx.currentTime;
    gainNode.gain.cancelScheduledValues(now);
    gainNode.gain.setValueAtTime(gainNode.gain.value, now);
    gainNode.gain.linearRampToValueAtTime(0.0001, now + FADE_SECONDS);
    setTimeout(() => {
      try { cleanupFn && cleanupFn(); } catch (_) { /* noop */ }
      try { gainNode.disconnect(); } catch (_) { /* noop */ }
    }, FADE_SECONDS * 1000 + 60);
    voiceRef.current = null;
  }, []);

  const rampVoice = useCallback((kind, targetValue, seconds = FADE_SECONDS) => {
    const voiceRef = kind === "primary" ? primaryRef : secondaryRef;
    if (!voiceRef.current || !ctxRef.current) return;
    const ctx = ctxRef.current;
    const g = voiceRef.current.gainNode.gain;
    const now = ctx.currentTime;
    g.cancelScheduledValues(now);
    g.setValueAtTime(g.value, now);
    g.linearRampToValueAtTime(targetValue, now + seconds);
  }, []);

  const start = useCallback(async () => {
    const ctx = ensureContext();
    if (!primaryRef.current) buildVoice("primary", primaryId);
    if (layerOn && !secondaryRef.current) buildVoice("secondary", secondaryId);
    if (ctx.state === "suspended") await ctx.resume();
    // Fade master up.
    const now = ctx.currentTime;
    masterRef.current.gain.cancelScheduledValues(now);
    masterRef.current.gain.setValueAtTime(masterRef.current.gain.value, now);
    masterRef.current.gain.linearRampToValueAtTime(masterTarget(), now + 2.4);
    // Fade voices up to their target levels.
    rampVoice("primary", voiceTarget("primary"), 2.0);
    if (layerOn) rampVoice("secondary", voiceTarget("secondary"), 2.0);
    setPlaying(true);
  }, [ensureContext, primaryId, secondaryId, layerOn, masterTarget, voiceTarget, buildVoice, rampVoice]);

  const stop = useCallback(() => {
    if (!ctxRef.current) return;
    const ctx = ctxRef.current;
    const now = ctx.currentTime;
    masterRef.current.gain.cancelScheduledValues(now);
    masterRef.current.gain.setValueAtTime(masterRef.current.gain.value, now);
    masterRef.current.gain.linearRampToValueAtTime(0.0001, now + 1.6);
    setPlaying(false);
  }, []);

  const toggle = () => {
    engage();
    return playing ? stop() : start();
  };

  // Switch the currently-focused voice (secondary when mix is on, else primary).
  const switchPreset = useCallback(
    async (direction) => {
      const kind = layerOn ? "secondary" : "primary";
      if (switchingRef.current[kind]) return;
      switchingRef.current[kind] = true;

      const currentId = kind === "primary" ? primaryId : secondaryId;
      const otherId = kind === "primary" ? secondaryId : primaryId;
      const currentIdx = getSoundPresetIndex(currentId);
      let nextIdx =
        (currentIdx + direction + SOUND_PRESETS.length) % SOUND_PRESETS.length;
      // When layered, skip past the other voice's preset so we never duplicate.
      if (layerOn && SOUND_PRESETS[nextIdx].id === otherId) {
        nextIdx =
          (nextIdx + direction + SOUND_PRESETS.length) % SOUND_PRESETS.length;
      }
      const nextId = SOUND_PRESETS[nextIdx].id;

      try {
        localStorage.setItem(
          kind === "primary" ? SOUND_STORAGE_KEY : SECONDARY_STORAGE_KEY,
          nextId,
        );
      } catch (_) { /* silent */ }

      // Not playing OR this voice not yet built: just swap state.
      const voiceRef = kind === "primary" ? primaryRef : secondaryRef;
      if (!playing || !voiceRef.current) {
        if (voiceRef.current) {
          try { voiceRef.current.cleanupFn && voiceRef.current.cleanupFn(); } catch (_) { /* noop */ }
          try { voiceRef.current.gainNode.disconnect(); } catch (_) { /* noop */ }
          voiceRef.current = null;
        }
        if (kind === "primary") setPrimaryId(nextId);
        else setSecondaryId(nextId);
        switchingRef.current[kind] = false;
        return;
      }

      // Playing: crossfade this voice only.
      rampVoice(kind, 0.0001, FADE_SECONDS);
      setTimeout(() => {
        if (voiceRef.current) {
          try { voiceRef.current.cleanupFn && voiceRef.current.cleanupFn(); } catch (_) { /* noop */ }
          try { voiceRef.current.gainNode.disconnect(); } catch (_) { /* noop */ }
          voiceRef.current = null;
        }
        if (kind === "primary") setPrimaryId(nextId);
        else setSecondaryId(nextId);
        switchingRef.current[kind] = false;
      }, FADE_SECONDS * 1000 + 50);
    },
    [primaryId, secondaryId, playing, layerOn, rampVoice],
  );

  // React to primary/secondary preset changes by rebuilding + ramping in.
  useEffect(() => {
    if (!ctxRef.current || !playing) return;
    if (primaryRef.current && primaryRef.current.presetId === primaryId) return;
    buildVoice("primary", primaryId);
    rampVoice("primary", voiceTarget("primary"), FADE_SECONDS);
  }, [primaryId, playing, buildVoice, rampVoice, voiceTarget]);

  useEffect(() => {
    if (!ctxRef.current || !playing || !layerOn) return;
    if (secondaryRef.current && secondaryRef.current.presetId === secondaryId) return;
    buildVoice("secondary", secondaryId);
    rampVoice("secondary", voiceTarget("secondary"), FADE_SECONDS);
  }, [secondaryId, playing, layerOn, buildVoice, rampVoice, voiceTarget]);

  const toggleLayer = () => {
    engage();
    setLayerOn((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(LAYER_STORAGE_KEY, String(next));
      } catch (_) { /* silent */ }
      if (!playing) return next;

      if (next) {
        // Turn ON: bring secondary in AND lower primary to mix level.
        buildVoice("secondary", secondaryId);
        rampVoice("secondary", SECONDARY_MIX_GAIN, FADE_SECONDS);
        rampVoice("primary", PRIMARY_MIX_GAIN, FADE_SECONDS);
      } else {
        // Turn OFF: fade out secondary AND restore primary to full.
        destroyVoice("secondary");
        rampVoice("primary", 1.0, FADE_SECONDS);
      }
      return next;
    });
  };

  const teardownRef = (voiceRef) => {
    if (!voiceRef.current) return;
    try { voiceRef.current.cleanupFn && voiceRef.current.cleanupFn(); } catch (_) { /* noop */ }
    try { voiceRef.current.gainNode.disconnect(); } catch (_) { /* noop */ }
    voiceRef.current = null;
  };

  // Apply a handpicked blend (both sounds + layer on) in one gesture.
  // Also washes the screen in the blend's matching light scene, if any.
  const applyBlend = useCallback(
    (blend) => {
      const scene = getSceneForBlend(blend.primary, blend.secondary);
      if (scene && props.onBlendScene) props.onBlendScene(scene);
      try {
        localStorage.setItem(SOUND_STORAGE_KEY, blend.primary);
        localStorage.setItem(SECONDARY_STORAGE_KEY, blend.secondary);
        localStorage.setItem(LAYER_STORAGE_KEY, "true");
      } catch (_) { /* silent */ }

      if (!playing) {
        teardownRef(primaryRef);
        teardownRef(secondaryRef);
        setPrimaryId(blend.primary);
        setSecondaryId(blend.secondary);
        setLayerOn(true);
        return;
      }

      // Playing: fade out both voices, then rebuild both with new IDs.
      rampVoice("primary", 0.0001, FADE_SECONDS);
      if (secondaryRef.current) rampVoice("secondary", 0.0001, FADE_SECONDS);

      setTimeout(() => {
        teardownRef(primaryRef);
        teardownRef(secondaryRef);
        buildVoice("primary", blend.primary);
        buildVoice("secondary", blend.secondary);
        rampVoice("primary", PRIMARY_MIX_GAIN, FADE_SECONDS);
        rampVoice("secondary", SECONDARY_MIX_GAIN, FADE_SECONDS);
        // Update state; effects short-circuit because refs already match.
        setPrimaryId(blend.primary);
        setSecondaryId(blend.secondary);
        setLayerOn(true);
      }, FADE_SECONDS * 1000 + 50);
    },
    [playing, buildVoice, rampVoice, props.onBlendScene],
  );

  // One-tap community preset: apply the blend and, if we're silent, start
  // playing within the same gesture so newcomers hear it immediately.
  const handleCommunityBlend = async (blend) => {
    engage();
    if (playing) {
      applyBlend(blend);
    } else {
      const scene = getSceneForBlend(blend.primary, blend.secondary);
      if (scene && props.onBlendScene) props.onBlendScene(scene);
      const ctx = ensureContext();
      teardownRef(primaryRef);
      teardownRef(secondaryRef);
      buildVoice("primary", blend.primary);
      buildVoice("secondary", blend.secondary);
      try {
        localStorage.setItem(SOUND_STORAGE_KEY, blend.primary);
        localStorage.setItem(SECONDARY_STORAGE_KEY, blend.secondary);
        localStorage.setItem(LAYER_STORAGE_KEY, "true");
      } catch (_) { /* silent */ }
      if (ctx.state === "suspended") {
        const p = ctx.resume();
        if (p && typeof p.catch === "function") p.catch(() => {});
      }
      const now = ctx.currentTime;
      masterRef.current.gain.cancelScheduledValues(now);
      masterRef.current.gain.setValueAtTime(masterRef.current.gain.value, now);
      masterRef.current.gain.linearRampToValueAtTime(masterTarget(), now + 2.4);
      rampVoice("primary", PRIMARY_MIX_GAIN, 2.0);
      rampVoice("secondary", SECONDARY_MIX_GAIN, 2.0);
      setPrimaryId(blend.primary);
      setSecondaryId(blend.secondary);
      setLayerOn(true);
      setPlaying(true);
    }
    logSoundEvent({
      eventType: "blend_apply",
      primaryId: blend.primary,
      secondaryId: blend.secondary,
      blendId: blend.source === "live" ? null : blend.id,
    });
  };

  const cycleBlend = () => {
    engage();
    const list = effectiveBlends;
    if (!list || list.length === 0) return;
    const currentIdx = list.findIndex(
      (b) => b.primary === primaryId && b.secondary === secondaryId,
    );
    const nextIdx = currentIdx < 0 ? 0 : (currentIdx + 1) % list.length;
    const nextBlend = list[nextIdx];
    applyBlend(nextBlend);
    // Opt-in telemetry: log the applied blend (silent when opted out).
    logSoundEvent({
      eventType: "blend_apply",
      primaryId: nextBlend.primary,
      secondaryId: nextBlend.secondary,
      blendId: nextBlend.source === "live" ? null : nextBlend.id,
    });
  };

  // Debounced organic-pair telemetry: 5s after the last change while in mix
  // mode, log whatever pair the user has landed on. Silent when opted out.
  useEffect(() => {
    if (!layerOn) return;
    const t = setTimeout(() => {
      logSoundEvent({
        eventType: "pair_set",
        primaryId,
        secondaryId,
        blendId: findMatchingBlend(primaryId, secondaryId)?.id || null,
      });
    }, 5000);
    return () => clearTimeout(t);
  }, [layerOn, primaryId, secondaryId]);

  // Live volume tracking (only while playing).
  useEffect(() => {
    if (!ctxRef.current || !masterRef.current || !playing) return;
    const ctx = ctxRef.current;
    const now = ctx.currentTime;
    masterRef.current.gain.cancelScheduledValues(now);
    masterRef.current.gain.setValueAtTime(masterRef.current.gain.value, now);
    masterRef.current.gain.linearRampToValueAtTime(masterTarget(), now + 0.6);
  }, [volume, playing, masterTarget]);

  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      try { primaryRef.current && primaryRef.current.cleanupFn && primaryRef.current.cleanupFn(); } catch (_) { /* noop */ }
      // eslint-disable-next-line react-hooks/exhaustive-deps
      try { secondaryRef.current && secondaryRef.current.cleanupFn && secondaryRef.current.cleanupFn(); } catch (_) { /* noop */ }
      try { ctxRef.current && ctxRef.current.close(); } catch (_) { /* noop */ }
    };
  }, []);

  // Imperative handle so App.js can trigger scene-based auto-coupling within
  // the same user-gesture stack as the scene click (needed for AudioContext
  // resume across browsers). Only fires if the user hasn't taken over audio.
  useImperativeHandle(
    ref,
    () => ({
      // One-tap return to last session's blend — must be called from within
      // a user gesture (toast action click) so the AudioContext may resume.
      resumeLastPlace: () => {
        if (playing) return;
        setUserEngagedAudio(true);
        start();
      },
      autoStartCoupled: (soundId) => {
        if (userEngagedRef.current) return; // user has taken control
        if (playing) return; // already producing sound — don't override
        if (!soundId) return;
        const ctx = ensureContext();
        // Rebuild primary with the coupled sound.
        if (primaryRef.current) {
          try { primaryRef.current.cleanupFn && primaryRef.current.cleanupFn(); } catch (_) { /* noop */ }
          try { primaryRef.current.gainNode.disconnect(); } catch (_) { /* noop */ }
          primaryRef.current = null;
        }
        buildVoice("primary", soundId);
        // Persist and update state.
        try { localStorage.setItem(SOUND_STORAGE_KEY, soundId); } catch (_) { /* silent */ }
        setPrimaryId(soundId);
        setAutoSoft(true);
        // Sync resume within the click gesture so browsers permit playback.
        if (ctx.state === "suspended") {
          const p = ctx.resume();
          if (p && typeof p.catch === "function") p.catch(() => {});
        }
        const now = ctx.currentTime;
        masterRef.current.gain.cancelScheduledValues(now);
        masterRef.current.gain.setValueAtTime(masterRef.current.gain.value, now);
        // Ramp master to the soft target (autoSoft is already reflected).
        const softTarget = (volume / 100) * 0.6 * 0.35;
        masterRef.current.gain.linearRampToValueAtTime(softTarget, now + 3.0);
        rampVoice("primary", 1.0, 2.4);
        setPlaying(true);

        // First-time hint — explain why audio started softly. Fires only
        // when the coupling actually started sound (not on skipped attempts),
        // slightly delayed so it doesn't compete with the visual scene change.
        if (!hasSeenCouplingHint()) {
          markCouplingHintSeen();
          setTimeout(() => {
            toast("the scene brought a matching sound", {
              description:
                "audio started softly. tap the pause button or slider any time to change it.",
              duration: 6500,
            });
          }, 900);
        }
      },
      // Long, gentle fade of the master output over the given duration,
      // then stop. Used by Sleep Space when a session gently auto-closes so
      // the audio fades in lockstep with the visual overlay instead of
      // cutting off. Silent no-op when audio isn't playing.
      fadeOutOverSeconds: (seconds) => {
        if (!ctxRef.current || !masterRef.current) return;
        if (!playing) return;
        const ctx = ctxRef.current;
        const dur = Math.max(1, Number(seconds) || 30);
        const now = ctx.currentTime;
        masterRef.current.gain.cancelScheduledValues(now);
        masterRef.current.gain.setValueAtTime(masterRef.current.gain.value, now);
        masterRef.current.gain.linearRampToValueAtTime(0.0001, now + dur);
        setTimeout(() => setPlaying(false), dur * 1000 + 80);
      },
      // Breath-synced ambient — a subtle multiplicative gain that swells on
      // the inhale, holds through the hold, and softens on the exhale so the
      // ears feel the same rhythm the eyes see (via the orb + colour pulse).
      // Modulation range is narrow on purpose (~0.7×–1.25×) so it reads as a
      // tide, never a volume knob turning. No-op if audio isn't playing.
      applyBreathModulation: (phase, seconds) => {
        if (!ctxRef.current || !breathModRef.current) return;
        if (!playing) return;
        const ctx = ctxRef.current;
        const dur = Math.max(0.6, Number(seconds) || 4);
        // Target multiplier per phase. Inhale + hold sit above 1.0 to feel
        // present, exhale dips below so the release lands.
        const targets = { inhale: 1.25, hold: 1.25, exhale: 0.7, rest: 1.0 };
        const target = targets[(phase || "").toLowerCase()] ?? 1.0;
        const now = ctx.currentTime;
        breathModRef.current.gain.cancelScheduledValues(now);
        breathModRef.current.gain.setValueAtTime(breathModRef.current.gain.value, now);
        breathModRef.current.gain.linearRampToValueAtTime(target, now + dur);
      },
      // Called when a breathing session ends (naturally or via close). Ramps
      // the modulation back to neutral over a short window so the ambient
      // bed returns to the user's chosen volume without a snap.
      endBreathModulation: () => {
        if (!ctxRef.current || !breathModRef.current) return;
        const ctx = ctxRef.current;
        const now = ctx.currentTime;
        breathModRef.current.gain.cancelScheduledValues(now);
        breathModRef.current.gain.setValueAtTime(breathModRef.current.gain.value, now);
        breathModRef.current.gain.linearRampToValueAtTime(1.0, now + 1.6);
      },
    }),
    [playing, ensureContext, buildVoice, rampVoice, volume, start],
  );

  const focusedName = layerOn ? secondary.name : primary.name;
  const activeBlend = layerOn
    ? effectiveBlends.find(
        (b) => b.primary === primary.id && b.secondary === secondary.id,
      ) || findMatchingBlend(primary.id, secondary.id)
    : null;
  const displayLabel = layerOn
    ? activeBlend
      ? activeBlend.name
      : `${primary.name} + ${secondary.name}`
    : primary.name;
  const displaySubLabel = layerOn && activeBlend
    ? `${primary.name} + ${secondary.name}`
    : null;

  // Arrival memory — remember whether sound was playing (skipping the mount
  // write so a fresh visit doesn't clobber last session's value) and the name
  // of the place it belonged to, so the app can offer a one-tap return.
  const playedOnceRef = useRef(false);
  useEffect(() => {
    if (!playedOnceRef.current && !playing) return;
    playedOnceRef.current = true;
    try {
      localStorage.setItem(WAS_PLAYING_KEY, String(playing));
      if (playing) localStorage.setItem(LAST_PLACE_NAME_KEY, displayLabel);
    } catch (_) { /* silent */ }
  }, [playing, displayLabel]);

  return (
    <div className="flex flex-col items-center gap-2.5 max-w-full">
      {liveTopLoaded && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.6, ease: "easeInOut", delay: 1.2 }}
          className="flex items-center gap-2 flex-wrap justify-center px-2"
          data-testid="community-presets"
        >
          <span className="text-[10px] tracking-[0.2em] uppercase text-white/35">
            loved by others
          </span>
          {effectiveBlends.slice(0, 3).map((b) => {
            const active =
              layerOn && b.primary === primaryId && b.secondary === secondaryId;
            return (
              <button
                key={`${b.primary}-${b.secondary}`}
                onClick={() => handleCommunityBlend(b)}
                className={`rounded-full px-3.5 py-1.5 text-xs tracking-wide backdrop-blur-xl border transition-colors duration-500 ${
                  active
                    ? "bg-white/15 border-white/25 text-white/95"
                    : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:text-white/90"
                }`}
                data-testid={`community-preset-${b.primary}-${b.secondary}`}
                data-active={active}
                aria-label={`Play the ${b.name} blend, loved by others`}
              >
                {b.name}
              </button>
            );
          })}
        </motion.div>
      )}

      <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.6, ease: "easeInOut", delay: 0.6 }}
      className="flex items-center gap-1.5 sm:gap-3 rounded-full backdrop-blur-xl bg-white/5 border border-white/10 px-2 sm:px-4 py-2.5 sm:py-3 shadow-[0_8px_32px_rgba(0,0,0,0.25)] max-w-full"
      data-testid="audio-controls"
    >
      <button
        onClick={() => switchPreset(-1)}
        className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors duration-500 shrink-0"
        data-testid="audio-prev-btn"
        aria-label={`Previous ${layerOn ? "layered" : "calming"} sound`}
      >
        <ChevronLeft size={16} strokeWidth={1.5} />
      </button>

      <button
        onClick={toggle}
        className="w-11 h-11 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 text-white/90 transition-colors duration-500 shrink-0"
        data-testid={playing ? "audio-pause-btn" : "audio-play-btn"}
        aria-label={playing ? "Pause calming sound" : "Play calming sound"}
      >
        {playing ? (
          <Pause size={18} strokeWidth={1.5} />
        ) : (
          <Play size={18} strokeWidth={1.5} className="ml-0.5" />
        )}
      </button>

      <button
        onClick={() => switchPreset(1)}
        className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors duration-500 shrink-0"
        data-testid="audio-next-btn"
        aria-label={`Next ${layerOn ? "layered" : "calming"} sound`}
      >
        <ChevronRight size={16} strokeWidth={1.5} />
      </button>

      <div className="hidden sm:flex flex-col text-left leading-tight pr-1 min-w-[150px]">
        <AnimatePresence mode="wait">
          <motion.span
            key={displayLabel}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.9, ease: "easeInOut" }}
            className="font-heading italic text-white/85 text-base whitespace-nowrap"
            data-testid="audio-preset-name"
            data-primary-id={primary.id}
            data-secondary-id={layerOn ? secondary.id : ""}
            data-focused-name={focusedName}
            data-blend-id={activeBlend ? activeBlend.id : ""}
          >
            {displayLabel}
          </motion.span>
        </AnimatePresence>
        <span className="text-[11px] tracking-widest uppercase text-white/40 whitespace-nowrap">
          {displaySubLabel ? (
            <span data-testid="audio-preset-sublabel">{displaySubLabel}</span>
          ) : (
            <>
              {playing ? "playing softly" : "silent"}
              {layerOn ? " · mix" : ""}
            </>
          )}
        </span>
      </div>

      <button
        onClick={cycleBlend}
        title="try a curated blend"
        className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-white/60 hover:text-white/95 hover:bg-white/10 transition-colors duration-500 shrink-0"
        data-testid="audio-blend-btn"
        aria-label="Try a curated blend"
      >
        <Sparkles size={15} strokeWidth={1.5} />
      </button>

      <button
        onClick={toggleLayer}
        aria-pressed={layerOn}
        title={layerOn ? "Playing two sounds" : "Add a second sound"}
        className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-colors duration-500 border shrink-0 ${
          layerOn
            ? "bg-white/15 border-white/25 text-white/95"
            : "bg-white/5 border-white/10 text-white/60 hover:text-white/85 hover:bg-white/10"
        }`}
        data-testid="audio-layer-toggle"
        data-active={layerOn}
      >
        <Layers size={15} strokeWidth={1.5} />
      </button>

      <div className="flex items-center gap-2 sm:gap-3 min-w-0 sm:min-w-[160px] flex-1 sm:flex-initial">
        <Volume2 size={16} strokeWidth={1.5} className="text-white/60 shrink-0 hidden sm:block" />
        <Slider
          value={[volume]}
          onValueChange={(v) => { engage(); setVolume(v[0]); }}
          max={100}
          step={1}
          className="w-full"
          data-testid="volume-slider"
          aria-label="Volume"
        />
      </div>
      </motion.div>
    </div>
  );
});

AudioControls.displayName = "AudioControls";

export default AudioControls;
